import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useStageScheduleChecklist, CONSTRUCTION_SUB_STAGE_CONFIG, UNASSIGNED_STAGE, isManualScheduleProjectType } from '@/hooks/useStageScheduleChecklist';
import type { StageScheduleItem, ConstructionSubStageData, StageData } from '@/hooks/useStageScheduleChecklist';
import { formatShortDate as fmtShort } from '@/lib/dateUtils';

const CANONICAL_STAGES = ['Planning', 'Consent', 'Construction', 'Handover', 'Completed'] as const;
type CanonicalStage = typeof CANONICAL_STAGES[number];

interface StagesTabProps {
  projectId: string;
}

export default function StagesTab({ projectId }: StagesTabProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const statusStyle: Record<string, { bg: string; text: string; border: string }> = {
    'Not Started': { bg: '#F9FAFB', text: '#9CA3AF', border: '#E5E7EB' },
    'In Progress': { bg: '#FEF0E7', text: '#E8621A', border: '#FDBA74' },
    'Completed':   { bg: '#F0FDF4', text: '#059669', border: '#BBF7D0' },
    'Delayed':     { bg: '#FEF2F2', text: '#EF4444', border: '#FECACA' },
  };

  const statusIcon: Record<string, string> = {
    'Not Started': 'ri-time-line',
    'In Progress': 'ri-loader-4-line',
    'Completed':   'ri-check-line',
    'Delayed':     'ri-error-warning-line',
  };

  const statusLabel: Record<string, string> = {
    'Not Started': t('stages.statusNotStarted'),
    'In Progress': t('stages.statusInProgress'),
    'Completed':   t('stages.statusCompleted'),
    'Delayed':     t('stages.statusDelayed'),
  };

  const {
    stages,
    loading,
    error,
    reload,
    suggestedCurrentStage,
    projectStage,
    updateProjectStage,
    projectType,
    movingUnassigned,
    moveUnassignedToConstruction,
  } = useStageScheduleChecklist(projectId);

  // ── All state hooks must be before any early returns ──
  const [expandedStage, setExpandedStage] = useState<string | null>(null);
  const [expandedSubStage, setExpandedSubStage] = useState<string | null>(null);
  const [showStagePrompt, setShowStagePrompt] = useState(false);
  const [pendingStageUpdate, setPendingStageUpdate] = useState<string | null>(null);
  const [showStageSelector, setShowStageSelector] = useState(false);
  const [showMoveConfirm, setShowMoveConfirm] = useState(false);
  const [pendingMoveStage, setPendingMoveStage] = useState<string | null>(null);
  const [moveConfirmMessage, setMoveConfirmMessage] = useState<string | null>(null);
  const [stageSaving, setStageSaving] = useState(false);
  const [stageToast, setStageToast] = useState<string | null>(null);
  const [stageError, setStageError] = useState<string | null>(null);

  // Track first load to avoid showing spinner on tab switch.
  const hasData = stages.some((s) => s.scheduleProgress.totalItems > 0);
  const [firstLoadDone, setFirstLoadDone] = useState(hasData || !loading);
  const prevLoadingRef = useRef(loading && !hasData);
  useEffect(() => {
    if (prevLoadingRef.current && !loading) setFirstLoadDone(true);
    prevLoadingRef.current = loading;
  }, [loading]);

  // Only show spinner on the very first load when there's no data yet
  const isFirstLoad = loading && !firstLoadDone && !hasData;
  const isBackgroundRefresh = loading && firstLoadDone;

  if (isFirstLoad) {
    return (
      <div className="flex items-center justify-center py-16">
        <i className="ri-loader-4-line animate-spin text-[#E8621A] text-3xl"></i>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 max-w-full overflow-x-hidden">
        <div className="bg-[#FEF2F2] border border-[#EF4444]/20 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#EF4444] flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-line text-white text-lg"></i>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-[#1A1A1A] font-bold text-sm">{t('stages.failedToLoadStages')}</h3>
              <p className="text-[#6B7280] text-xs mt-1 leading-relaxed">{error}</p>
              <button
                onClick={reload}
                className="mt-3 inline-flex items-center gap-2 bg-[#E8621A] text-white px-4 py-2.5 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>{t('stages.retryBtn')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Manual stage control helpers ──
  const requestMoveStage = (newStage: string) => {
    const newIdx = CANONICAL_STAGES.indexOf(newStage as CanonicalStage);
    if (newIdx <= 0) {
      void handleMoveStage(newStage);
      return;
    }

    const incompleteEarlier = CANONICAL_STAGES.slice(0, newIdx).filter((stageName) => {
      const stageData = stages.find((s) => s.name === stageName);
      return stageData != null && stageData.scheduleProgress.unfinishedItems > 0;
    });

    if (incompleteEarlier.length > 0) {
      setPendingMoveStage(newStage);
      setMoveConfirmMessage(
        t('stages.moveStageAnywayConfirm', {
          stages: incompleteEarlier.join(', '),
          stage: newStage,
        })
      );
      setShowMoveConfirm(true);
      setShowStageSelector(false);
      return;
    }

    void handleMoveStage(newStage);
  };

  const handleMoveStage = async (newStage: string) => {
    setStageSaving(true);
    setStageError(null);
    const ok = await updateProjectStage(newStage);
    setStageSaving(false);
    if (ok) {
      setStageToast(`${t('stages.movedToStageToast', { stage: newStage })}`);
      setShowStageSelector(false);
      setShowMoveConfirm(false);
      setPendingMoveStage(null);
      setMoveConfirmMessage(null);
      setTimeout(() => setStageToast(null), 3000);
    } else {
      setStageError(t('stages.stageUpdateFailed'));
      setTimeout(() => setStageError(null), 5000);
    }
  };

  const unassignedStage = stages.find((s) => s.name === UNASSIGNED_STAGE) ?? null;
  const showMoveUnassignedButton =
    isManualScheduleProjectType(projectType) &&
    (unassignedStage?.scheduleProgress.totalItems ?? 0) > 0;

  const handleMoveUnassignedToConstruction = async () => {
    setStageError(null);
    const result = await moveUnassignedToConstruction();
    if (result.success) {
      if (result.count > 0) {
        setStageToast(t('stages.movedUnassignedToConstructionToast', { count: result.count }));
        setTimeout(() => setStageToast(null), 4000);
      }
      reload();
    } else {
      setStageError(result.error || t('stages.moveUnassignedFailed'));
      setTimeout(() => setStageError(null), 5000);
    }
  };

  const currentIdx = CANONICAL_STAGES.indexOf((projectStage || '') as CanonicalStage);
  const prevStage = currentIdx > 0 ? CANONICAL_STAGES[currentIdx - 1] : null;
  const nextStage = currentIdx < CANONICAL_STAGES.length - 1 ? CANONICAL_STAGES[currentIdx + 1] : null;

  const stagesNeedingUpdate = stages.filter((s) => {
    if (s.status !== 'Completed') return false;
    const stageIndex = CANONICAL_STAGES.indexOf(s.name as CanonicalStage);
    if (stageIndex === -1 || stageIndex === CANONICAL_STAGES.length - 1) return false;
    const nextStageName = CANONICAL_STAGES[stageIndex + 1];
    return projectStage !== nextStageName;
  });

  return (
    <div className="space-y-4 max-w-full overflow-x-hidden">
      {isBackgroundRefresh && (
        <div className="flex-shrink-0 bg-[#FEF0E7]/60 border border-[#FDBA74]/30 rounded-xl px-3 py-1.5 flex items-center justify-center gap-2">
          <i className="ri-loader-4-line animate-spin text-[#E8621A] text-xs"></i>
          <span className="text-[#92400E] text-[11px] font-medium">{t('checklist.refreshingData')}</span>
        </div>
      )}
      {stageError && (
        <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-xl px-4 py-3 text-sm text-[#EF4444]">
          {stageError}
        </div>
      )}

      {/* Suggested current stage banner */}
      {suggestedCurrentStage && projectStage !== suggestedCurrentStage && stagesNeedingUpdate.length > 0 && (
        <div className="bg-[#FEF0E7] rounded-2xl border border-[#FDBA74] p-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#FEF0E7] border border-[#FDBA74] flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-[#E8621A] text-lg"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[#1A1A1A] text-sm font-semibold">
                {t('stages.suggestedCurrentStageLabel')}: <span className="font-bold text-[#E8621A]">{suggestedCurrentStage}</span>
              </p>
              <p className="text-[#92400E] text-xs mt-0.5 leading-relaxed">
                {t('stages.scheduleProgressSuggestsMoving', { stage: suggestedCurrentStage })}
              </p>
              <button
                onClick={() => {
                  setPendingStageUpdate(suggestedCurrentStage);
                  setShowStagePrompt(true);
                }}
                className="mt-2 inline-flex items-center gap-2 bg-[#E8621A] text-white px-4 py-2 rounded-xl text-xs font-bold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-right-line"></i>{t('stages.updateProjectStageBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Manual Stage Control Panel ── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FEF0E7] flex items-center justify-center flex-shrink-0">
              <i className="ri-flag-2-line text-[#E8621A] text-lg"></i>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-0.5">
                {t('stages.currentStageLabel')}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold text-[#1A1A1A]">
                  {projectStage || t('stages.notSet')}
                </span>
                {projectStage && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#FEF0E7] text-[#E8621A]">
                    {t('stages.currentLabel')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Move controls */}
          <div className="flex items-center gap-2 flex-wrap">
            {prevStage && (
              <button
                onClick={() => requestMoveStage(prevStage)}
                disabled={stageSaving}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 bg-white text-[#374151] text-xs font-bold cursor-pointer whitespace-nowrap hover:border-gray-300 disabled:opacity-50"
              >
                <i className="ri-arrow-left-line text-sm"></i>
                {prevStage}
              </button>
            )}

            {/* Stage dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowStageSelector((v) => !v)}
                disabled={stageSaving}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 bg-white text-[#374151] text-xs font-bold cursor-pointer whitespace-nowrap hover:border-gray-300 disabled:opacity-50"
              >
                <i className="ri-expand-up-down-line text-sm"></i>
                {t('stages.setStageBtn')}
                <i className={`${showStageSelector ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`}></i>
              </button>
              {showStageSelector && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-100 rounded-xl z-30 min-w-[160px] overflow-hidden" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {CANONICAL_STAGES.map((s) => (
                    <button
                      key={s}
                      onClick={() => requestMoveStage(s)}
                      className={`w-full text-left px-4 py-2.5 text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-[#FEF0E7] hover:text-[#E8621A] transition-colors ${
                        s === projectStage ? 'bg-[#FEF0E7] text-[#E8621A]' : 'text-[#374151]'
                      }`}
                    >
                      {s === projectStage && <i className="ri-check-line mr-1.5"></i>}
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {nextStage && (
              <button
                onClick={() => requestMoveStage(nextStage)}
                disabled={stageSaving}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#E8621A] text-white text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#d45a18] disabled:opacity-50"
              >
                {nextStage}
                <i className="ri-arrow-right-line text-sm"></i>
              </button>
            )}
          </div>
        </div>

        {/* Stage progress indicator */}
        <div className="mt-3 flex items-center gap-1 flex-wrap">
          {CANONICAL_STAGES.map((s, idx) => {
            const isActive = s === projectStage;
            const isPast = currentIdx !== -1 && idx < currentIdx;
            return (
              <div key={s} className="flex items-center gap-1">
                <div
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold transition-all ${
                    isActive
                      ? 'bg-[#E8621A] text-white'
                      : isPast
                      ? 'bg-[#F0FDF4] text-[#059669]'
                      : 'bg-gray-100 text-[#9CA3AF]'
                  }`}
                >
                  {isPast && <i className="ri-check-line text-[9px]"></i>}
                  {s}
                </div>
                {idx < CANONICAL_STAGES.length - 1 && (
                  <i className="ri-arrow-right-s-line text-[#D1D5DB] text-xs"></i>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Stage cards — always show ALL canonical stages */}
      {CANONICAL_STAGES.map((stageName) => {
        const stageData = stages.find((s) => s.name === stageName) || null;
        return (
          <StageCard
            key={stageName}
            stageName={stageName}
            stage={stageData}
            isExpanded={expandedStage === stageName}
            expandedSubStage={expandedSubStage}
            projectStage={projectStage}
            onToggle={() => setExpandedStage(expandedStage === stageName ? null : stageName)}
            onToggleSubStage={(subName) => setExpandedSubStage(expandedSubStage === subName ? null : subName)}
            statusStyle={statusStyle}
            statusIcon={statusIcon}
            statusLabel={statusLabel}
            lang={lang}
            t={t}
          />
        );
      })}

      {unassignedStage && unassignedStage.scheduleProgress.totalItems > 0 && (
        <StageCard
          key={UNASSIGNED_STAGE}
          stageName={UNASSIGNED_STAGE}
          stage={unassignedStage}
          isExpanded={expandedStage === UNASSIGNED_STAGE}
          expandedSubStage={expandedSubStage}
          projectStage={projectStage}
          onToggle={() => setExpandedStage(expandedStage === UNASSIGNED_STAGE ? null : UNASSIGNED_STAGE)}
          onToggleSubStage={(subName) => setExpandedSubStage(expandedSubStage === subName ? null : subName)}
          statusStyle={statusStyle}
          statusIcon={statusIcon}
          statusLabel={statusLabel}
          lang={lang}
          t={t}
          actionButton={showMoveUnassignedButton ? {
            label: t('stages.moveAllToConstructionBtn'),
            onClick: () => { void handleMoveUnassignedToConstruction(); },
            loading: movingUnassigned,
          } : undefined}
        />
      )}

      {/* Stage update confirmation modal (from suggested stage banner) */}
      {showStagePrompt && pendingStageUpdate && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setShowStagePrompt(false); setPendingStageUpdate(null); }}></div>
          <div className="relative bg-white w-full max-w-sm rounded-3xl overflow-hidden mx-4">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-[#1A1A1A] font-bold text-base">{t('stages.updateStageTitle')}</h3>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-[#6B7280] text-sm leading-relaxed">
                {t('stages.updateStageConfirm', { stage: pendingStageUpdate })}
              </p>
              <p className="text-[#9CA3AF] text-xs">
                {t('stages.updateStageDesc')}
              </p>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
              <button
                onClick={() => { setShowStagePrompt(false); setPendingStageUpdate(null); }}
                className="flex-1 py-3 rounded-xl bg-white border border-gray-200 text-[#374151] text-sm font-bold cursor-pointer whitespace-nowrap"
              >
                {t('stages.cancelBtn')}
              </button>
              <button
                onClick={async () => {
                  const ok = await updateProjectStage(pendingStageUpdate);
                  if (ok) {
                    setShowStagePrompt(false);
                    setPendingStageUpdate(null);
                    setStageToast(t('stages.movedToStageToast', { stage: pendingStageUpdate }));
                    setTimeout(() => setStageToast(null), 3000);
                  }
                }}
                className="flex-1 py-3 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap"
              >
                {t('stages.updateStageBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual move with incomplete earlier stages */}
      {showMoveConfirm && pendingMoveStage && moveConfirmMessage && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setShowMoveConfirm(false); setPendingMoveStage(null); setMoveConfirmMessage(null); }}></div>
          <div className="relative bg-white w-full max-w-sm rounded-3xl overflow-hidden mx-4">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-[#1A1A1A] font-bold text-base">{t('stages.moveStageAnywayTitle')}</h3>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-[#6B7280] text-sm leading-relaxed">{moveConfirmMessage}</p>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
              <button
                onClick={() => { setShowMoveConfirm(false); setPendingMoveStage(null); setMoveConfirmMessage(null); }}
                className="flex-1 py-3 rounded-xl bg-white border border-gray-200 text-[#374151] text-sm font-bold cursor-pointer whitespace-nowrap"
              >
                {t('stages.cancelBtn')}
              </button>
              <button
                onClick={() => pendingMoveStage && void handleMoveStage(pendingMoveStage)}
                disabled={stageSaving}
                className="flex-1 py-3 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {t('stages.moveStageAnywayBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click-away for stage dropdown */}
      {showStageSelector && (
        <div className="fixed inset-0 z-20" onClick={() => setShowStageSelector(false)}></div>
      )}

      {/* Toast */}
      {stageToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] bg-[#1A1A1A] text-white text-sm font-semibold px-5 py-3 rounded-2xl flex items-center gap-2 whitespace-nowrap">
          <i className="ri-checkbox-circle-line text-[#4ADE80] text-base"></i>
          {stageToast}
        </div>
      )}
    </div>
  );
}

/* ── Stage Card ── */
function StageCard({
  stageName,
  stage,
  isExpanded,
  expandedSubStage,
  projectStage,
  onToggle,
  onToggleSubStage,
  statusStyle,
  statusIcon,
  statusLabel,
  lang,
  t,
  actionButton,
}: {
  stageName: string;
  stage: StageData | null;
  isExpanded: boolean;
  expandedSubStage: string | null;
  projectStage: string | null;
  onToggle: () => void;
  onToggleSubStage: (subName: string) => void;
  statusStyle: Record<string, { bg: string; text: string; border: string }>;
  statusIcon: Record<string, string>;
  statusLabel: Record<string, string>;
  lang: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
  actionButton?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
  };
}) {
  const STAGE_CFG: Record<string, { icon: string; accent: string; bg: string }> = {
    Planning:     { icon: 'ri-map-pin-line',         accent: '#D97706', bg: '#FFFBEB' },
    Consent:      { icon: 'ri-file-shield-line',     accent: '#E8621A', bg: '#FEF0E7' },
    Construction: { icon: 'ri-building-2-line',      accent: '#059669', bg: '#F0FDF4' },
    Handover:     { icon: 'ri-key-2-line',           accent: '#10B981', bg: '#ECFDF5' },
    Completed:    { icon: 'ri-checkbox-circle-line', accent: '#059669', bg: '#F0FDF4' },
    [UNASSIGNED_STAGE]: { icon: 'ri-question-line', accent: '#6B7280', bg: '#F9FAFB' },
  };
  const cfg = STAGE_CFG[stageName] || { icon: 'ri-flag-line', accent: '#9CA3AF', bg: '#F9FAFB' };

  const canonicalOrder = ['Planning', 'Consent', 'Construction', 'Handover', 'Completed'];
  const currentIdx = canonicalOrder.indexOf(projectStage || '');
  const thisIdx = canonicalOrder.indexOf(stageName);

  // Derive display status: use real data when available, otherwise infer from position
  let displayStatus: string;
  if (stage && stage.scheduleProgress.totalItems > 0) {
    displayStatus = stage.status;
  } else if (projectStage === stageName) {
    displayStatus = 'In Progress';
  } else if (currentIdx !== -1 && thisIdx < currentIdx) {
    displayStatus = 'Completed';
  } else {
    displayStatus = 'Not Started';
  }

  const st = statusStyle[displayStatus] || statusStyle['Not Started'];
  const sp = stage?.scheduleProgress ?? { totalItems: 0, completedItems: 0, unfinishedItems: 0, progressPercentage: 0, totalProgress: 0 };

  const accentColor = stage?.accentColor ?? cfg.accent;
  const bgColor = stage?.bgColor ?? cfg.bg;
  const icon = stage?.icon ?? cfg.icon;
  const checklistCount = stage?.checklistCount ?? 0;
  const completedChecklistCount = stage?.completedChecklistCount ?? 0;
  const checklistCompletionPct = stage?.checklistCompletionPct ?? 0;
  const checklistFinalPct = stage?.checklistFinalPct ?? 0;
  const scheduleBinaryPct = stage?.scheduleBinaryPct ?? 0;
  const combinedProgress = stage?.combinedProgress ?? 0;
  const hasItems = sp.totalItems > 0;

  return (
    <div className={`bg-white rounded-2xl border overflow-hidden transition-all ${
      projectStage === stageName ? 'border-[#E8621A]/40' : 'border-gray-100'
    }`}>
      {/* Main stage header */}
      <button onClick={onToggle} className="w-full text-left px-4 py-4 cursor-pointer">
        <div className="flex items-start gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: bgColor }}
          >
            <i className={`${icon} text-lg`} style={{ color: accentColor }}></i>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-[#1A1A1A] text-sm font-bold">
                  {stageName === UNASSIGNED_STAGE ? t('stages.unassignedStage') : stageName}
                </span>
                {projectStage === stageName && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#FEF0E7] text-[#E8621A]">
                    {t('stages.currentLabel')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded-full border"
                  style={{ backgroundColor: st.bg, color: st.text, borderColor: st.border }}
                >
                  <i className={`${statusIcon[displayStatus] || 'ri-time-line'} mr-1 text-[9px]`}></i>
                  {statusLabel[displayStatus] || displayStatus}
                </span>
                <i className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#9CA3AF] text-lg`}></i>
              </div>
            </div>

            {/* No items placeholder */}
            {!hasItems && stageName === UNASSIGNED_STAGE && (
              <p className="text-[10px] text-[#9CA3AF] mt-1.5">{t('stages.unassignedDesc')}</p>
            )}
            {!hasItems && stageName !== UNASSIGNED_STAGE && (
              <p className="text-[10px] text-[#9CA3AF] mt-1.5">{t('stages.noItemsYet')}</p>
            )}

            {/* Stats when items exist */}
            {hasItems && (
              <div className="flex items-center gap-4 mt-2.5 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <i className="ri-calendar-todo-line text-[#9CA3AF] text-xs"></i>
                  <span className="text-xs text-[#374151]">
                    <span className="font-bold text-[#059669]">{sp.completedItems}</span>
                    {' / '}
                    <span className="font-bold">{sp.totalItems}</span> {t('stages.scheduleCompleteLabel')}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="ri-percent-line text-[#9CA3AF] text-xs"></i>
                  <span className="text-xs text-[#374151] font-bold">{sp.progressPercentage}% {t('stages.scheduleLabel')}</span>
                </div>
                {checklistCount > 0 && (
                  <div className="flex items-center gap-1.5">
                    <i className="ri-list-check text-[#9CA3AF] text-xs"></i>
                    <span className="text-xs text-[#374151]">
                      <span className="font-bold">{completedChecklistCount}</span>
                      {' / '}
                      <span className="font-bold">{checklistCount}</span> {t('stages.checklistFinalLabel')}
                      {' '}
                      <span className="text-[#059669] font-bold">({checklistCompletionPct}%)</span>
                    </span>
                  </div>
                )}
                {stage?.plannedStartDate && stage?.plannedEndDate && (
                  <div className="flex items-center gap-1.5">
                    <i className="ri-calendar-line text-[#9CA3AF] text-xs"></i>
                    <span className="text-xs text-[#374151]">
                      {fmtShort(stage.plannedStartDate, lang)} — {fmtShort(stage.plannedEndDate, lang)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Combined stage progress bar */}
            {hasItems && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold text-[#9CA3AF]">{t('stages.combinedProgressLabel')}</span>
                  <span className="text-[10px] font-bold" style={{ color: combinedProgress === 100 ? '#059669' : '#E8621A' }}>
                    {combinedProgress}%
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${combinedProgress}%`, backgroundColor: combinedProgress === 100 ? '#059669' : accentColor }}
                  ></div>
                </div>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  <span className="text-[9px] text-[#9CA3AF]">
                    {t('stages.scheduleLabel')}: <span className="font-bold text-[#374151]">{scheduleBinaryPct}%</span>
                    {' '}({sp.completedItems}/{sp.totalItems} items)
                  </span>
                  {checklistCount > 0 ? (
                    <span className="text-[9px] text-[#9CA3AF]">
                      {t('stages.checklistFinalLabel')}: <span className="font-bold text-[#059669]">{checklistFinalPct}%</span>
                      {' '}({completedChecklistCount}/{checklistCount} items)
                    </span>
                  ) : (
                    <span className="text-[9px] font-bold text-[#F59E0B] bg-[#FFFBEB] px-1.5 py-0.5 rounded-full">
                      {t('stages.noChecklistMappedLabel')}
                    </span>
                  )}
                  {checklistCount > 0 && (
                    <span className="text-[9px] text-[#9CA3AF]">
                      {t('stages.formulaLabel', { sched: scheduleBinaryPct, cl: checklistFinalPct, combined: combinedProgress })}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Checklist final progress bar */}
            {checklistCount > 0 && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold text-[#9CA3AF]">{t('stages.checklistFinalApprovalLabel')}</span>
                  <span className="text-[10px] font-bold text-[#374151]">{checklistFinalPct}%</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${checklistFinalPct}%`, backgroundColor: '#059669' }}
                  ></div>
                </div>
              </div>
            )}

            {(stage?.scheduleItems ?? []).some((i) => i.delay_status === 'delayed' || i.delay_status === 'completed_late') && (
              <div className="mt-2 flex items-center gap-1.5">
                <i className="ri-error-warning-line text-[#EF4444] text-xs"></i>
                <span className="text-[10px] font-bold text-[#EF4444]">{t('stages.oneOrMoreDelayedLabel')}</span>
              </div>
            )}
          </div>
        </div>
      </button>

      {actionButton && hasItems && (
        <div className="px-4 pb-3 border-b border-gray-50">
          <button
            type="button"
            onClick={actionButton.onClick}
            disabled={actionButton.loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#059669] text-white text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#047857] disabled:opacity-50"
          >
            {actionButton.loading ? (
              <i className="ri-loader-4-line animate-spin text-sm"></i>
            ) : (
              <i className="ri-arrow-right-circle-line text-sm"></i>
            )}
            {actionButton.label}
          </button>
        </div>
      )}

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-gray-50">
          {/* Construction: show sub-stage cards first */}
          {stageName === 'Construction' && (stage?.constructionSubStages ?? []).length > 0 && (
            <div className="px-4 pt-3 pb-1">
              <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">{t('stages.paymentMilestonesLabel')}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(stage!.constructionSubStages!).map((sub) => (
                  <SubStageCard
                    key={sub.name}
                    sub={sub}
                    isExpanded={expandedSubStage === sub.name}
                    onToggle={() => onToggleSubStage(sub.name)}
                    statusStyle={statusStyle}
                    statusLabel={statusLabel}
                    lang={lang}
                    t={t}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Show schedule items */}
          {stageName === 'Construction' && expandedSubStage ? (
            (() => {
              const sub = stage?.constructionSubStages?.find((s) => s.name === expandedSubStage);
              if (!sub) return null;
              return (
                <div className="px-4 py-2">
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      onClick={() => onToggleSubStage(expandedSubStage)}
                      className="text-[10px] font-bold text-[#9CA3AF] hover:text-[#374151] cursor-pointer flex items-center gap-1"
                    >
                      <i className="ri-arrow-left-line"></i>{t('stages.backToAllLabel')}
                    </button>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {sub.scheduleItems.map((item, idx) => (
                      <ScheduleItemRow key={item.id} item={item} index={idx} accent={sub.accentColor} lang={lang} t={t} />
                    ))}
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="divide-y divide-gray-50">
              {(stage?.scheduleItems ?? []).length === 0 ? (
                <div className="px-4 py-4 text-center">
                  <p className="text-[#9CA3AF] text-xs">{t('stages.noItemsYet')}</p>
                </div>
              ) : (
                (stage!.scheduleItems).map((item, idx) => (
                  <ScheduleItemRow key={item.id} item={item} index={idx} accent={accentColor} lang={lang} t={t} />
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Sub-Stage Card ── */
function SubStageCard({
  sub,
  isExpanded,
  onToggle,
  statusStyle,
  statusLabel,
  lang,
  t,
}: {
  sub: ConstructionSubStageData;
  isExpanded: boolean;
  onToggle: () => void;
  statusStyle: Record<string, { bg: string; text: string; border: string }>;
  statusLabel: Record<string, string>;
  lang: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const cfg = CONSTRUCTION_SUB_STAGE_CONFIG[sub.name];
  const st = statusStyle[sub.status] || statusStyle['Not Started'];
  const sp = sub.scheduleProgress;

  return (
    <button
      onClick={onToggle}
      className={`text-left rounded-xl border p-3 cursor-pointer transition-all hover:shadow-sm ${isExpanded ? 'border-gray-300' : 'border-gray-100'}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: cfg.bgColor }}
        >
          <i className={`${cfg.icon} text-sm`} style={{ color: cfg.accentColor }}></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-[#1A1A1A]">{sub.name}</p>
          <div className="flex items-center gap-1.5">
            <span
              className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border"
              style={{ backgroundColor: st.bg, color: st.text, borderColor: st.border }}
            >
              {statusLabel[sub.status] || sub.status}
            </span>
            {cfg.paymentPercent > 0 && (
              <span className="text-[9px] font-bold text-[#0891B2]">{t('stagesCard.paymentPercent', { n: cfg.paymentPercent })}</span>
            )}
            {cfg.paymentPercent === 0 && (
              <span className="text-[9px] font-bold text-[#9CA3AF]">{t('stages.nilPayment')}</span>
            )}
          </div>
        </div>
      </div>

      {/* Combined progress bar */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-[9px] text-[#9CA3AF]">{t('stages.combinedProgressLabel')}</span>
          <span className="text-[9px] font-bold" style={{ color: sub.combinedProgress === 100 ? '#059669' : cfg.accentColor }}>
            {sub.combinedProgress}%
          </span>
        </div>
        <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${sub.combinedProgress}%`, backgroundColor: sub.combinedProgress === 100 ? '#059669' : cfg.accentColor }}
          ></div>
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="text-[8px] text-[#9CA3AF]">{t('stages.scheduleLabel')}: {sub.scheduleBinaryPct}%</span>
          {sub.checklistCount > 0 && (
            <span className="text-[8px] text-[#9CA3AF]">{t('stages.checklistFinalLabel')}: {sub.checklistFinalPct}%</span>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-3 flex-wrap">
        {sub.plannedStartDate && sub.plannedEndDate && (
          <span className="text-[9px] text-[#9CA3AF]">
            {fmtShort(sub.plannedStartDate, lang)} — {fmtShort(sub.plannedEndDate, lang)}
          </span>
        )}
        {sub.checklistCount > 0 && (
          <span className="text-[9px] text-[#9CA3AF]">
            {sub.completedChecklistCount}/{sub.checklistCount} {t('stages.checklistFinalLabel')}
          </span>
        )}
      </div>

      {isExpanded && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <span className="text-[9px] text-[#E8621A] font-bold flex items-center gap-1">
            <i className="ri-arrow-down-line"></i>{t('stages.showingItemsLabel')}
          </span>
        </div>
      )}
    </button>
  );
}

/* ── Schedule Item Row ── */
function ScheduleItemRow({
  item,
  index,
  accent,
  lang,
  t,
}: {
  item: StageScheduleItem;
  index: number;
  accent: string;
  lang: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const pct = item.is_complete ? 100 : item.progress;
  const done = item.is_complete;

  return (
    <div className="px-4 py-3 flex items-start gap-3">
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold border-2"
        style={{
          backgroundColor: done ? '#F0FDF4' : '#F9FAFB',
          borderColor: done ? '#BBF7D0' : '#E5E7EB',
          color: done ? '#059669' : '#9CA3AF',
        }}
      >
        {done ? (
          <i className="ri-check-line text-[10px]"></i>
        ) : (
          item.task_no ?? index + 1
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold leading-snug ${done ? 'text-[#9CA3AF] line-through' : 'text-[#1A1A1A]'}`}>
          {item.title}
        </p>

        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {item.start_date && item.end_date && (
            <span className="text-[10px] text-[#9CA3AF]">
              {fmtShort(item.start_date, lang)} — {fmtShort(item.end_date, lang)}
            </span>
          )}
          <span className="text-[10px] font-medium" style={{ color: accent }}>
            {pct}% {t('stages.scheduleLabel')}
          </span>
          {item.is_complete && (
            <span className="text-[10px] font-bold text-[#059669] bg-[#F0FDF4] px-1.5 py-0.5 rounded-full">{t('stages.completeLabel')}</span>
          )}
          {item.delay_status === 'delayed' && (
            <span className="text-[10px] font-bold text-[#EF4444] bg-[#FEF2F2] px-1.5 py-0.5 rounded-full">{t('stages.delayedLabel')}</span>
          )}
          {item.delay_status === 'at_risk' && (
            <span className="text-[10px] font-bold text-[#F59E0B] bg-[#FFFBEB] px-1.5 py-0.5 rounded-full">{t('stages.atRiskLabel')}</span>
          )}
        </div>

        {/* Approval unit counts */}
        {(item.managementCheckedCount !== undefined || item.subcontractorCheckedCount !== undefined) && (
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {item.managementCheckedCount !== undefined && (item.managementCheckedCount + (item.missingManagement ?? 0)) > 0 && (
              <span className="text-[9px] font-bold text-[#059669] bg-[#F0FDF4] px-1.5 py-0.5 rounded-full flex items-center gap-1">
                <i className="ri-check-line text-[8px]"></i>
                Mgmt {item.managementCheckedCount}/{item.managementCheckedCount + (item.missingManagement ?? 0)}
              </span>
            )}
            {item.subcontractorCheckedCount !== undefined && (item.subcontractorCheckedCount + (item.missingSubcontractor ?? 0)) > 0 && (
              <span className="text-[9px] font-bold text-[#0891B2] bg-[#F0F9FF] px-1.5 py-0.5 rounded-full flex items-center gap-1">
                <i className="ri-check-line text-[8px]"></i>
                Sub {item.subcontractorCheckedCount}/{item.subcontractorCheckedCount + (item.missingSubcontractor ?? 0)}
              </span>
            )}
            {item.missingManagement > 0 && (
              <span className="text-[9px] font-bold text-[#F59E0B] bg-[#FFFBEB] px-1.5 py-0.5 rounded-full">
                {item.missingManagement} {t('stages.mgmtPendingLabel')}
              </span>
            )}
            {item.missingSubcontractor > 0 && (
              <span className="text-[9px] font-bold text-[#F59E0B] bg-[#FFFBEB] px-1.5 py-0.5 rounded-full">
                {item.missingSubcontractor} {t('stages.subPendingLabel')}
              </span>
            )}
          </div>
        )}

        {item.linkedChecklistCount > 0 && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <i className="ri-list-check text-[#9CA3AF] text-[10px]"></i>
            <span className="text-[10px] text-[#9CA3AF]">
              {item.linkedChecklistCount} {t('stages.checklistItemsLabel')}
              {' '}
              <span className="text-[#059669] font-medium">
                · {item.completedChecklistCount} {t('stages.completedLabel')}
              </span>
            </span>
          </div>
        )}

        {/* Final progress reason badge */}
        <div className="flex items-center gap-1.5 mt-1">
          {item.finalProgressReason === 'not_mapped' && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#FFFBEB] text-[#F59E0B]">
              {t('stages.notMappedLabel')}
            </span>
          )}
          {item.finalProgressReason === 'checklist_complete' && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#F0FDF4] text-[#059669]">
              {t('stages.checklistCompleteLabel')}
            </span>
          )}
          {item.finalProgressReason === 'checklist_incomplete' && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#FEF0E7] text-[#E8621A]">
              {t('stages.checklistIncompleteLabel')}
            </span>
          )}
          {item.finalProgressReason === 'manual_complete' && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#F0FDF4] text-[#059669]">
              {t('stages.manuallyCompletedLabel')}
            </span>
          )}
          {item.finalProgressReason === 'manual_incomplete' && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-[#9CA3AF]">
              {t('stages.manualLabel', { pct: item.progress })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}